/**
 * Activity Helpers — Shared utilities for Parent, Teacher & Admin portals
 *
 * Derives activity data from questionStates timestamps.
 * All functions are pure and can be memoized with useMemo.
 *
 * Created as part of Portal Redesign Plan v2.0, Phase 0.
 */

import { AppState, ModuleId, QuestionState, FamilyMasteryState } from '../types'
import { MODULES } from '../data/questionLoader'
import { getModuleMasteryFromFamilies } from './masteryCanonical'

// ============================================================
// Types
// ============================================================

export interface DayActivity {
  /** Day label in Dutch (Ma, Di, Wo, Do, Vr, Za, Zo) */
  label: string
  /** Whether any practice happened on this day */
  practiced: boolean
  /** Number of questions answered on this day */
  questionCount: number
}

export interface PracticeSession {
  /** Start timestamp of the session */
  date: number
  /** Module practiced (most frequent in session) */
  moduleId: ModuleId | null
  /** Module display title */
  moduleTitle: string
  /** Module icon emoji */
  moduleIcon: string
  /** Total questions in session */
  questionCount: number
  /** Correct answers in session */
  correctCount: number
  /** Accuracy percentage 0-100 */
  accuracy: number
}

export type TrendDirection = 'up' | 'stable' | 'down'

export interface Milestone {
  type: 'level_up' | 'subject_complete' | 'streak' | 'first_practice'
  label: string
  emoji: string
  timestamp: number
}

// ============================================================
// Constants
// ============================================================

const DAY_LABELS = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo']
const MS_PER_DAY = 24 * 60 * 60 * 1000
const SESSION_GAP_MS = 30 * 60 * 1000 // 30 minutes between sessions

// ============================================================
// Weekly Activity
// ============================================================

/**
 * Get weekly activity for a learner (Mon-Sun of current week).
 * Derives data from questionStates history timestamps.
 *
 * @returns Array of 7 DayActivity objects (Mon through Sun)
 */
export function getWeeklyActivityForLearner(
  questionStates: Record<string, QuestionState>,
): DayActivity[] {
  const now = new Date()
  // Get Monday of current week
  const dayOfWeek = now.getDay() // 0=Sun, 1=Mon, ...
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
  const monday = new Date(now)
  monday.setHours(0, 0, 0, 0)
  monday.setDate(monday.getDate() + mondayOffset)
  const mondayTs = monday.getTime()

  // Initialize 7 days
  const days: DayActivity[] = DAY_LABELS.map((label) => ({
    label,
    practiced: false,
    questionCount: 0,
  }))

  // Scan all question states for activity this week
  for (const state of Object.values(questionStates)) {
    if (!state.history || state.history.length === 0) continue

    for (const attempt of state.history) {
      const ts = attempt.timestamp
      if (ts < mondayTs) continue // Before this week

      const dayIndex = Math.floor((ts - mondayTs) / MS_PER_DAY)
      if (dayIndex >= 0 && dayIndex < 7) {
        days[dayIndex].practiced = true
        days[dayIndex].questionCount++
      }
    }
  }

  return days
}

/**
 * Get total practice days this week.
 */
export function getWeeklyPracticeDayCount(
  questionStates: Record<string, QuestionState>,
): number {
  return getWeeklyActivityForLearner(questionStates).filter((d) => d.practiced)
    .length
}

// ============================================================
// Recent Sessions
// ============================================================

/**
 * Get recent practice sessions for a learner.
 * Groups consecutive answers (within 30-min windows) into "sessions".
 *
 * @param questionStates - The learner's question states
 * @param limit - Max sessions to return (default 5)
 * @returns Array of PracticeSession objects, most recent first
 */
export function getRecentSessionsForLearner(
  questionStates: Record<string, QuestionState>,
  limit: number = 5,
): PracticeSession[] {
  // Collect all individual attempts with their question context
  const allAttempts: Array<{
    timestamp: number
    isCorrect: boolean
    questionId: string
    moduleId: ModuleId | null
  }> = []

  for (const [key, state] of Object.entries(questionStates)) {
    if (!state.history || state.history.length === 0) continue

    // Extract base questionId (remove _g6, _g7, _g8 suffix)
    const baseQuestionId = state.questionId || key.replace(/_g\d+$/, '')

    // Determine module from questionId prefix
    const moduleId = getModuleFromQuestionId(baseQuestionId)

    for (const attempt of state.history) {
      allAttempts.push({
        timestamp: attempt.timestamp,
        isCorrect: attempt.isCorrect,
        questionId: baseQuestionId,
        moduleId,
      })
    }
  }

  if (allAttempts.length === 0) return []

  // Sort by timestamp descending
  allAttempts.sort((a, b) => b.timestamp - a.timestamp)

  // Group into sessions (gap > 30 min = new session)
  const sessions: PracticeSession[] = []
  let currentSession: typeof allAttempts = [allAttempts[0]]

  for (let i = 1; i < allAttempts.length; i++) {
    const gap =
      currentSession[currentSession.length - 1].timestamp -
      allAttempts[i].timestamp

    if (gap > SESSION_GAP_MS) {
      // Finalize current session
      sessions.push(buildSession(currentSession))
      if (sessions.length >= limit) break
      currentSession = [allAttempts[i]]
    } else {
      currentSession.push(allAttempts[i])
    }
  }

  // Don't forget the last session
  if (currentSession.length > 0 && sessions.length < limit) {
    sessions.push(buildSession(currentSession))
  }

  return sessions.slice(0, limit)
}

function buildSession(
  attempts: Array<{
    timestamp: number
    isCorrect: boolean
    questionId: string
    moduleId: ModuleId | null
  }>,
): PracticeSession {
  // Find most frequent module in session
  const moduleCounts = new Map<string, number>()
  for (const a of attempts) {
    if (a.moduleId) {
      moduleCounts.set(a.moduleId, (moduleCounts.get(a.moduleId) || 0) + 1)
    }
  }

  let topModuleId: ModuleId | null = null
  let topCount = 0
  for (const [mod, count] of moduleCounts) {
    if (count > topCount) {
      topModuleId = mod as ModuleId
      topCount = count
    }
  }

  const moduleData = topModuleId
    ? MODULES.find((m) => m.id === topModuleId)
    : null
  const correctCount = attempts.filter((a) => a.isCorrect).length

  return {
    date: attempts[0].timestamp, // Most recent attempt in session
    moduleId: topModuleId,
    moduleTitle: moduleData?.title || 'Gemengd',
    moduleIcon: moduleData?.icon || '📚',
    questionCount: attempts.length,
    correctCount,
    accuracy:
      attempts.length > 0
        ? Math.round((correctCount / attempts.length) * 100)
        : 0,
  }
}

/**
 * Derive moduleId from questionId prefix.
 * Question IDs follow pattern: MODULE-FAMILY-VARIANT (e.g., GET-BREUK-BASIS-001)
 */
function getModuleFromQuestionId(questionId: string): ModuleId | null {
  const prefix = questionId.split('-')[0]?.toUpperCase()
  const prefixToModule: Record<string, ModuleId> = {
    GET: 'Getalbegrip',
    MET: 'Metriek',
    TGL: 'TijdGeld',
    SWW: 'SpellingWW',
    SPR: 'SpellingRegel',
    TAA: 'Taalbegrip',
    STU: 'Studievaardig',
    TOPO: 'Topo',
  }
  return prefixToModule[prefix] || null
}

// ============================================================
// Mastery Trends
// ============================================================

/**
 * Calculate mastery trend for a module.
 * Compares recent activity (last 7 days) accuracy to overall accuracy.
 *
 * @returns 'up' if recent > overall + 5%, 'down' if recent < overall - 5%, else 'stable'
 */
export function calculateMasteryTrend(
  familyMastery: Record<string, FamilyMasteryState>,
  moduleId: ModuleId,
  group: number,
): TrendDirection {
  const now = Date.now()
  const weekAgo = now - 7 * MS_PER_DAY

  let recentCorrect = 0
  let recentTotal = 0
  let overallCorrect = 0
  let overallTotal = 0

  for (const [key, state] of Object.entries(familyMastery)) {
    // Check if this family belongs to the module (key format: familyId_g6)
    const familyModule = getModuleFromQuestionId(state.familyId)
    if (familyModule !== moduleId) continue

    // Check group scope
    if (!key.includes(`_g${group}`)) continue

    overallCorrect += state.correct
    overallTotal += state.attempts

    // Count recent attempts from history
    if (state.history) {
      for (const attempt of state.history) {
        if (attempt.timestamp >= weekAgo) {
          recentTotal++
          if (attempt.isCorrect) recentCorrect++
        }
      }
    }
  }

  // Not enough data for trend
  if (overallTotal < 5 || recentTotal < 3) return 'stable'

  const overallAccuracy = overallCorrect / overallTotal
  const recentAccuracy = recentCorrect / recentTotal
  const delta = recentAccuracy - overallAccuracy

  if (delta > 0.05) return 'up'
  if (delta < -0.05) return 'down'
  return 'stable'
}

/**
 * Calculate per-module trends for all modules.
 */
export function getAllModuleTrends(
  familyMastery: Record<string, FamilyMasteryState>,
  group: number,
): Record<ModuleId, TrendDirection> {
  const result: Partial<Record<ModuleId, TrendDirection>> = {}
  for (const module of MODULES) {
    result[module.id] = calculateMasteryTrend(familyMastery, module.id, group)
  }
  return result as Record<ModuleId, TrendDirection>
}

// ============================================================
// Active Users (Admin)
// ============================================================

/**
 * Count active users within a time window.
 *
 * @param accounts - All accounts
 * @param getLearnerData - Function to get learner data by ID
 * @param hoursWindow - Time window in hours (default 24)
 * @returns Number of learners with activity in the window
 */
export function getActiveUsersCount(
  accountsById: Record<string, { id: string; role: string }>,
  getLearnerData: (id: string) => AppState | null,
  hoursWindow: number = 24,
): number {
  const cutoff = Date.now() - hoursWindow * 60 * 60 * 1000
  let count = 0

  for (const account of Object.values(accountsById)) {
    if (account.role !== 'learner') continue

    const data = getLearnerData(account.id)
    if (!data?.questionStates) continue

    // Check if any question was answered after cutoff
    const hasRecentActivity = Object.values(data.questionStates).some(
      (state) => state.lastAttemptDate > cutoff,
    )

    if (hasRecentActivity) count++
  }

  return count
}

// ============================================================
// Weakest Family in Module (Teacher)
// ============================================================

/**
 * Find the weakest family within a module for a specific learner.
 * Used by AttentionCards to give specific pedagogical hints.
 */
export function getWeakestFamilyInModule(
  familyMastery: Record<string, FamilyMasteryState>,
  moduleId: ModuleId,
  group: number,
): { familyId: string; mastery: number; label: string } | null {
  let weakest: { familyId: string; mastery: number } | null = null

  for (const [key, state] of Object.entries(familyMastery)) {
    const familyModule = getModuleFromQuestionId(state.familyId)
    if (familyModule !== moduleId) continue
    if (!key.includes(`_g${group}`)) continue
    if (state.attempts < 2) continue // Need minimum data

    if (!weakest || state.masteryScore < weakest.mastery) {
      weakest = { familyId: state.familyId, mastery: state.masteryScore }
    }
  }

  if (!weakest) return null

  // Create human-readable label from familyId
  // e.g., "SWW-TT-STAM" → "Tegenwoordige tijd - stamregel"
  const label = humanizeFamilyId(weakest.familyId)

  return { ...weakest, label }
}

function humanizeFamilyId(familyId: string): string {
  // Remove module prefix and join remaining parts
  const parts = familyId.split('-').slice(1)
  return parts
    .join(' ')
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase())
}

// ============================================================
// Recent Milestones (Parent)
// ============================================================

/**
 * Detect recent milestones for a learner.
 * Checks for level-ups, subject completions, and practice streaks.
 */
export function getRecentMilestones(
  learnerData: AppState,
  group: number,
): Milestone[] {
  const milestones: Milestone[] = []
  const now = Date.now()
  const weekAgo = now - 7 * MS_PER_DAY

  // Check for high mastery subjects (≥95% = "Klaar!")
  const familyMastery = learnerData.familyMastery || {}
  for (const module of MODULES) {
    const mastery = getModuleMasteryFromFamilies(
      familyMastery,
      module.id,
      group,
    )
    if (mastery >= 95) {
      milestones.push({
        type: 'subject_complete',
        label: `${module.title} is klaar! ⭐`,
        emoji: '🏆',
        timestamp: now, // We don't track exact completion time
      })
    }
  }

  // Check for practice streak (consecutive days this week)
  const weeklyActivity = getWeeklyActivityForLearner(
    learnerData.questionStates || {},
  )
  let currentStreak = 0
  let maxStreak = 0
  for (const day of weeklyActivity) {
    if (day.practiced) {
      currentStreak++
      maxStreak = Math.max(maxStreak, currentStreak)
    } else {
      currentStreak = 0
    }
  }
  if (maxStreak >= 3) {
    milestones.push({
      type: 'streak',
      label: `${maxStreak} dagen achter elkaar geoefend!`,
      emoji: '🔥',
      timestamp: now,
    })
  }

  // Check for any practice this week (first practice milestone)
  const totalThisWeek = weeklyActivity.reduce(
    (sum, d) => sum + d.questionCount,
    0,
  )
  if (totalThisWeek > 0 && totalThisWeek <= 10) {
    milestones.push({
      type: 'first_practice',
      label: `${totalThisWeek} vragen beantwoord deze week`,
      emoji: '📝',
      timestamp: now,
    })
  }

  return milestones
}

// ============================================================
// Weekly Stats Summary (for WeeklyHighlights)
// ============================================================

export interface WeeklyStats {
  totalQuestions: number
  practiceDays: number
  bestModuleId: ModuleId | null
  bestModuleTitle: string
  bestModuleIcon: string
  bestModuleCount: number
}

/**
 * Get weekly practice statistics for a learner.
 */
export function getWeeklyStats(
  questionStates: Record<string, QuestionState>,
): WeeklyStats {
  const now = new Date()
  const dayOfWeek = now.getDay()
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
  const monday = new Date(now)
  monday.setHours(0, 0, 0, 0)
  monday.setDate(monday.getDate() + mondayOffset)
  const mondayTs = monday.getTime()

  const moduleCounts = new Map<ModuleId, number>()
  const practiceDays = new Set<number>()
  let totalQuestions = 0

  for (const [key, state] of Object.entries(questionStates)) {
    if (!state.history) continue

    const baseId = state.questionId || key.replace(/_g\d+$/, '')
    const moduleId = getModuleFromQuestionId(baseId)

    for (const attempt of state.history) {
      if (attempt.timestamp < mondayTs) continue

      totalQuestions++
      const dayIndex = Math.floor((attempt.timestamp - mondayTs) / MS_PER_DAY)
      practiceDays.add(dayIndex)

      if (moduleId) {
        moduleCounts.set(moduleId, (moduleCounts.get(moduleId) || 0) + 1)
      }
    }
  }

  // Find best module
  let bestModuleId: ModuleId | null = null
  let bestCount = 0
  for (const [mod, count] of moduleCounts) {
    if (count > bestCount) {
      bestModuleId = mod
      bestCount = count
    }
  }

  const bestModule = bestModuleId
    ? MODULES.find((m) => m.id === bestModuleId)
    : null

  return {
    totalQuestions,
    practiceDays: practiceDays.size,
    bestModuleId,
    bestModuleTitle: bestModule?.title || '',
    bestModuleIcon: bestModule?.icon || '',
    bestModuleCount: bestCount,
  }
}
